// Practice-timezone-aware date/time derivation for the schedule calendar
// (Task 6). FullCalendar (with the luxon3 plugin) hands callbacks real UTC
// `Date` instants — not "fake UTC" wall-clock-as-UTC values — so converting
// them to the practice's local date/time is a plain `setZone`. Verified
// empirically: rendering FullCalendar with `timeZone="America/Denver"` and
// reading `datesSet`'s `arg.start.toISOString()` returns the true UTC
// instant of local midnight, not local midnight relabeled as UTC.
import { DateTime } from "luxon";

// Practice-timezone-local ISO date ("YYYY-MM-DD") for a UTC Date instant.
export function isoDateInZone(date: Date, tz: string): string {
  return DateTime.fromJSDate(date).setZone(tz).toISODate()!;
}

// Practice-timezone-local "HH:mm" for a UTC Date instant.
export function timeOfDayInZone(date: Date, tz: string): string {
  return DateTime.fromJSDate(date).setZone(tz).toFormat("HH:mm");
}

// A drag-selection or dragged/resized event's `end` sitting exactly on
// local midnight (a whole-day span, or a drop that lands on the next day's
// 00:00) formats as "00:00" via timeOfDayInZone — which would read as
// "ends before it starts" once paired with a same-or-later start_time.
// Treat that boundary as the end of the *current* day instead, matching
// the all-day block convention (00:00-23:59) used elsewhere.
export function clampMidnightEnd(time: string): string {
  return time === "00:00" ? "23:59" : time;
}

// FullCalendar's `datesSet` reports a half-open [start, end) range. The
// admin schedule/appointments queries want an *inclusive* last visible day
// (`toDate`) — computed in the practice timezone, not the browser's, so a
// UTC-midnight `end` boundary doesn't roll forward a day in a
// negative-offset zone (the bug this replaces: slicing
// `arg.end.toISOString()` directly reads the date one day late for
// America/Denver).
// Zone-aware UTC ISO instant bounds for a practice-local date range
// (inclusive fromDate/toDate, both "YYYY-MM-DD") — used by the admin
// appointments query. `${toDate}T23:59:59.999Z` (raw UTC) reads the wrong
// window in every zone with a non-zero offset: for a negative-offset zone
// like America/Denver it cuts off before the practice day actually ends
// (e.g. an evening booking on the last visible day, whose UTC instant
// falls the next UTC calendar day, gets excluded).
export function appointmentQueryBoundsInZone(
  fromDate: string,
  toDate: string,
  tz: string,
): { from: string; to: string } {
  return {
    from: DateTime.fromISO(fromDate, { zone: tz }).startOf("day").toUTC()
      .toISO()!,
    to: DateTime.fromISO(toDate, { zone: tz }).endOf("day").toUTC().toISO()!,
  };
}

export function scheduleRangeFromDatesSet(
  start: Date,
  end: Date,
  tz: string,
): { fromDate: string; toDate: string } {
  return {
    fromDate: isoDateInZone(start, tz),
    toDate: DateTime.fromJSDate(end).setZone(tz).minus({ milliseconds: 1 })
      .toISODate()!,
  };
}
